/**
 * Shared Access Form Component
 * 
 * Form for creating or editing a shared access (secondary user).
 * Includes account permissions selection.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedAccessStore, useWorkspaceStore } from '../../store';
import { Modal, Button, Input } from '../ui';
import type {
  SharedAccess,
  AccountPermission,
  AccountPermissionLevel,
  FeaturePermissionLevel,
  FeaturePermissions,
  BankAccount,
  CreateSharedAccessRequest,
  UpdateSharedAccessRequest,
} from '@budgira/shared';

interface SharedAccessFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingAccess: SharedAccess | null;
  accounts: BankAccount[];
}

const SharedAccessForm = ({
  isOpen,
  onClose,
  onSuccess,
  editingAccess,
  accounts,
}: SharedAccessFormProps) => {
  const { t } = useTranslation();
  const { createSharedAccess, updateSharedAccess, isLoading, error, clearError } = useSharedAccessStore();
  const { currentWorkspace } = useWorkspaceStore();
  const errorRef = useRef<HTMLDivElement>(null);
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions>({
    categories: 'none',
    paymentMethods: 'none',
    checkbooks: 'none',
    projects: 'none',
    reconciliation: 'none',
    statements: 'none',
    budgets: 'none',
    apiKeys: 'none',
    alerts: 'none',
    counterparties: 'none',
  });
  const [accountPermissions, setAccountPermissions] = useState<AccountPermission[]>([]);
  const [sendInvitation, setSendInvitation] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Get accounts for the current workspace (used for permissions)
  const workspaceAccounts = accounts;
  
  // Scroll to error when it appears
  useEffect(() => {
    if ((formError || error || emailError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [formError, error, emailError]);
  
  // Check if error is email-related (for displaying near email field)
  const isEmailRelatedError = (err: string | null): boolean => {
    if (!err) return false;
    const emailErrorPatterns = [
      'email',
      'e-mail',
      'adresse',
      'compte budgira',
      'active budgira',
      'propriétaire',
      'owner',
      'already exists',
      'existe déjà',
    ];
    return emailErrorPatterns.some(pattern => 
      err.toLowerCase().includes(pattern.toLowerCase())
    );
  };
  
  // Reset form when modal opens/closes or editingAccess changes
  useEffect(() => {
    if (isOpen) {
      clearError();
      setFormError(null);
      setEmailError(null);
      
      if (editingAccess) {
        setFirstName(editingAccess.firstName);
        setLastName(editingAccess.lastName);
        setEmail(editingAccess.email);
        setSelectedWorkspaceId(editingAccess.workspaceId);
        // Use featurePermissions if available, fallback to legacy boolean fields
        setFeaturePermissions(editingAccess.featurePermissions ?? {
          categories: editingAccess.canManageCategories ? 'write' : 'none',
          paymentMethods: editingAccess.canManagePaymentMethods ? 'write' : 'none',
          checkbooks: 'none',
          projects: 'none',
          reconciliation: 'none',
          statements: 'none',
          budgets: 'none',
          apiKeys: 'none',
          alerts: 'none',
          counterparties: 'none',
        });
        setAccountPermissions(editingAccess.accountPermissions);
      } else {
        setFirstName('');
        setLastName('');
        setEmail('');
        // Always use current workspace - no choice offered
        setSelectedWorkspaceId(currentWorkspace?.id || '');
        setFeaturePermissions({
          categories: 'none',
          paymentMethods: 'none',
          checkbooks: 'none',
          projects: 'none',
          reconciliation: 'none',
          statements: 'none',
          budgets: 'none',
          apiKeys: 'none',
          alerts: 'none',
          counterparties: 'none',
        });
        // Initialize all accounts with 'none' permission
        setAccountPermissions(
          accounts.map(account => ({
            accountId: account.id,
            permission: 'none' as AccountPermissionLevel,
          }))
        );
        setSendInvitation(true);
      }
    }
  }, [isOpen, editingAccess, accounts, clearError, currentWorkspace]);
  
  // Handle store error - check if it's email-related
  useEffect(() => {
    if (error && isEmailRelatedError(error)) {
      setEmailError(error);
      clearError(); // Clear the global error since we're showing it locally
    }
  }, [error, clearError]);
  
  // Update permissions for a specific account
  const handlePermissionChange = (accountId: string, permission: AccountPermissionLevel) => {
    setAccountPermissions(prev =>
      prev.map(ap =>
        ap.accountId === accountId ? { ...ap, permission } : ap
      )
    );
  };
  
  // Update feature permission
  const handleFeaturePermissionChange = (feature: keyof FeaturePermissions, value: FeaturePermissionLevel) => {
    setFeaturePermissions(prev => ({
      ...prev,
      [feature]: value,
    }));
  };
  
  // Validate form
  const validateForm = (): boolean => {
    if (!firstName.trim()) {
      setFormError(t('settings.sharedAccess.form.errors.firstNameRequired'));
      return false;
    }
    if (!lastName.trim()) {
      setFormError(t('settings.sharedAccess.form.errors.lastNameRequired'));
      return false;
    }
    if (!editingAccess && !email.trim()) {
      setFormError(t('settings.sharedAccess.form.errors.emailRequired'));
      return false;
    }
    if (!editingAccess && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError(t('settings.sharedAccess.form.errors.emailInvalid'));
      return false;
    }
    if (!editingAccess && !selectedWorkspaceId) {
      setFormError(t('settings.sharedAccess.form.errors.workspaceRequired'));
      return false;
    }
    
    // Check at least one account has read or write permission
    const hasAnyPermission = accountPermissions.some(ap => ap.permission !== 'none');
    if (!hasAnyPermission) {
      setFormError(t('settings.sharedAccess.form.errors.noPermissions'));
      return false;
    }
    
    return true;
  };
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setEmailError(null);
    
    if (!validateForm()) {
      return;
    }
    
    if (editingAccess) {
      // Update existing access
      const updateData: UpdateSharedAccessRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        featurePermissions,
        accountPermissions,
      };
      
      const result = await updateSharedAccess(editingAccess.id, updateData);
      if (result) {
        onSuccess();
      }
    } else {
      // Create new access
      const createData: CreateSharedAccessRequest = {
        workspaceId: selectedWorkspaceId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        featurePermissions,
        accountPermissions,
      };
      
      const result = await createSharedAccess(createData, sendInvitation);
      if (result) {
        onSuccess();
      }
    }
  };
  
  // Get permission for an account
  const getPermission = (accountId: string): AccountPermissionLevel => {
    const ap = accountPermissions.find(p => p.accountId === accountId);
    return ap?.permission || 'none';
  };
  
  // Radio button for permission selection
  const PermissionRadio = ({
    accountId,
    value,
    label,
  }: {
    accountId: string;
    value: AccountPermissionLevel;
    label: string;
  }) => {
    const isSelected = getPermission(accountId) === value;
    return (
      <label className="flex items-center cursor-pointer">
        <input
          type="radio"
          name={`permission-${accountId}`}
          value={value}
          checked={isSelected}
          onChange={() => handlePermissionChange(accountId, value)}
          className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
        />
        <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">{label}</span>
      </label>
    );
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingAccess
        ? t('settings.sharedAccess.form.editTitle')
        : t('settings.sharedAccess.form.addTitle')
      }
      size="lg"
      closeOnBackdropClick={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t('common.saving')
              : editingAccess
                ? t('common.save')
                : t('settings.sharedAccess.form.create')
            }
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General error messages (non-email related) */}
        {(formError || (error && !isEmailRelatedError(error))) && (
          <div ref={errorRef} className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
            {formError || error}
          </div>
        )}
        
        {/* Personal information */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('settings.sharedAccess.form.firstName')}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            disabled={isLoading}
            required
          />
          <Input
            label={t('settings.sharedAccess.form.lastName')}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>
        
        {/* Email (only for new access) */}
        {!editingAccess && (
          <div>
            <Input
              label={t('settings.sharedAccess.form.email')}
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setEmailError(null); // Clear email error when user types
              }}
              disabled={isLoading}
              required
              helperText={!emailError ? t('settings.sharedAccess.form.emailHelper') : undefined}
            />
            {/* Email-specific error displayed right below the field (not using Input's error prop to have custom styling) */}
            {emailError && (
              <div ref={errorRef} className="mt-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                {emailError}
              </div>
            )}
          </div>
        )}
        
        {/* Workspace display (read-only - always uses current workspace) */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
            {t('settings.sharedAccess.form.workspace')}
          </label>
          <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-300">
            {currentWorkspace?.name || t('common.loading')}
          </div>
        </div>
        
        {/* Email display (for editing) */}
        {editingAccess && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
              {t('settings.sharedAccess.form.email')}
            </label>
            <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-300">
              {editingAccess.email}
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {t('settings.sharedAccess.form.emailReadonly')}
            </p>
          </div>
        )}
        
        {/* Feature permissions */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            {t('settings.sharedAccess.form.featurePermissions')}
          </label>
          
          <div className="space-y-3">
            {/* Categories */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.categories')}
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-categories"
                      value={level}
                      checked={featurePermissions.categories === level}
                      onChange={() => handleFeaturePermissionChange('categories', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.paymentMethods')}
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-paymentMethods"
                      value={level}
                      checked={featurePermissions.paymentMethods === level}
                      onChange={() => handleFeaturePermissionChange('paymentMethods', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Checkbooks */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.checkbooks')}
                <span className="ml-2 text-xs font-normal text-violet-600 dark:text-violet-400">Premium</span>
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-checkbooks"
                      value={level}
                      checked={featurePermissions.checkbooks === level}
                      onChange={() => handleFeaturePermissionChange('checkbooks', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Projects */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.projects')}
                <span className="ml-2 text-xs font-normal text-violet-600 dark:text-violet-400">Premium</span>
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-projects"
                      value={level}
                      checked={featurePermissions.projects === level}
                      onChange={() => handleFeaturePermissionChange('projects', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Reconciliation */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.reconciliation')}
                <span className="ml-2 text-xs font-normal text-violet-600 dark:text-violet-400">Premium</span>
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-reconciliation"
                      value={level}
                      checked={featurePermissions.reconciliation === level}
                      onChange={() => handleFeaturePermissionChange('reconciliation', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Statements (Bilans) */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.statements')}
                <span className="ml-2 text-xs font-normal text-violet-600 dark:text-violet-400">Premium</span>
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-statements"
                      value={level}
                      checked={featurePermissions.statements === level}
                      onChange={() => handleFeaturePermissionChange('statements', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Budgets */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.budgets')}
                <span className="ml-2 text-xs font-normal text-violet-600 dark:text-violet-400">Premium</span>
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-budgets"
                      value={level}
                      checked={featurePermissions.budgets === level}
                      onChange={() => handleFeaturePermissionChange('budgets', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* API Keys */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.apiKeys')}
                <span className="ml-2 text-xs font-normal text-violet-600 dark:text-violet-400">Premium</span>
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-apiKeys"
                      value={level}
                      checked={featurePermissions.apiKeys === level}
                      onChange={() => handleFeaturePermissionChange('apiKeys', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Alerts */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.alerts')}
              </div>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-alerts"
                      value={level}
                      checked={featurePermissions.alerts === level}
                      onChange={() => handleFeaturePermissionChange('alerts', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Counterparties (Tiers) */}
            <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
              <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('settings.sharedAccess.features.counterparties')}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                {t('settings.sharedAccess.features.counterpartiesHint')}
              </p>
              <div className="flex items-center gap-6">
                {(['none', 'read', 'write'] as FeaturePermissionLevel[]).map(level => (
                  <label key={level} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="feature-counterparties"
                      value={level}
                      checked={featurePermissions.counterparties === level}
                      onChange={() => handleFeaturePermissionChange('counterparties', level)}
                      disabled={isLoading}
                      className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 focus:ring-violet-500 dark:bg-slate-700"
                    />
                    <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                      {t(`settings.sharedAccess.permission.${level}`)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Account permissions */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">
            {t('settings.sharedAccess.form.accountPermissions')}
          </label>
          
          {workspaceAccounts.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 italic">
              {t('settings.sharedAccess.form.noAccounts')}
            </p>
          ) : (
            <div className="space-y-3">
              {workspaceAccounts.map(account => (
                <div
                  key={account.id}
                  className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600"
                >
                  <div className="font-medium text-slate-900 dark:text-slate-100 mb-2">
                    {account.name}
                    <span className="text-slate-500 dark:text-slate-400 font-normal ml-2">
                      ({account.bankName})
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <PermissionRadio
                      accountId={account.id}
                      value="none"
                      label={t('settings.sharedAccess.permission.none')}
                    />
                    <PermissionRadio
                      accountId={account.id}
                      value="read"
                      label={t('settings.sharedAccess.permission.read')}
                    />
                    <PermissionRadio
                      accountId={account.id}
                      value="write"
                      label={t('settings.sharedAccess.permission.write')}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Send invitation option (only for new access) */}
        {!editingAccess && (
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendInvitation}
                onChange={e => setSendInvitation(e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 text-violet-600 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500 dark:bg-slate-700"
              />
              <span className="ml-2 text-sm text-slate-700 dark:text-slate-200">
                {t('settings.sharedAccess.form.sendInvitation')}
              </span>
            </label>
            <p className="mt-1 ml-6 text-xs text-slate-500 dark:text-slate-400">
              {t('settings.sharedAccess.form.sendInvitationHelper')}
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default SharedAccessForm;
